import { useCallback, useEffect, useRef, useState } from 'react'
import { ReloadOutlined } from '@ant-design/icons'
import { Button } from '@crosscode/gravity-components'
import { useTrassirControlWs } from '@/features/trassir/useTrassirControlWs'
import { useJsmpegPlayer } from '@/features/trassir/useJsmpegPlayer'
import { trassirWsUrl } from '@/features/trassir/utils'
import { useVideoViewport } from '@/features/trassir/useVideoViewport'
import { VideoViewportControls } from '@/features/trassir/VideoViewportControls'
import { VideoQualityControls } from '@/features/trassir/VideoQualityControls'
import {
  getVideoQualityPreset,
  readStoredVideoQuality,
  saveStoredVideoQuality,
  type VideoQualityId,
} from '@/features/trassir/videoQuality'
import styles from './CashCameraLive.module.css'
import vpStyles from '@/features/trassir/VideoViewport.module.css'

export type CashCameraClipRequest = {
  start: string
  stop: string
  startUs: number
  stopUs: number
  /** ActivePOS: µs уже «стена» TZ сервера — бэкенд не должен +utcOffset. */
  localAsUtc?: boolean
  /** Меняется при каждом клике, чтобы перезапускать тот же интервал. */
  key: number
}

interface Props {
  serverId: number
  channelGuid: string
  channelName?: string | null
  clipRequest?: CashCameraClipRequest | null
  onClipEnded?: () => void
  /** Без внешних отступов — для встраивания в панель разбора */
  compact?: boolean
}

export function CashCameraLive({
  serverId,
  channelGuid,
  channelName,
  clipRequest = null,
  onClipEnded,
  compact = false,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const clipKeyRef = useRef<number | null>(null)
  const archiveEndHandledRef = useRef(0)
  const [dragging, setDragging] = useState(false)
  const [chromePinned, setChromePinned] = useState(false)
  const [hasFrame, setHasFrame] = useState(false)
  const [quality, setQuality] = useState<VideoQualityId>(readStoredVideoQuality)
  const {
    connected,
    status,
    activeStream,
    error,
    archiveEndSeq,
    selectLiveCamera,
    startArchivePlayback,
    reconnect,
  } = useTrassirControlWs(serverId, true)

  const startLive = useCallback(
    (guid: string, qualityId: VideoQualityId = quality) => {
      const preset = getVideoQualityPreset(qualityId)
      selectLiveCamera(guid, preset.options)
    },
    [quality, selectLiveCamera],
  )

  const {
    viewportRef,
    zoom,
    isFullscreen,
    isZoomed,
    toggleFullscreen,
    resetView,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onDoubleClick,
    stageStyle,
  } = useVideoViewport()

  const streamUrl = activeStream?.streamId
    ? trassirWsUrl(serverId, activeStream.streamId)
    : null

  useEffect(() => {
    setHasFrame(false)
  }, [streamUrl])

  useJsmpegPlayer(canvasRef, streamUrl, {
    onFirstFrame: () => setHasFrame(true),
  })

  useEffect(() => {
    resetView()
  }, [streamUrl, resetView])

  const isArchive = activeStream?.mode === 'archive'
  const streamReady = !!streamUrl
  const playing = streamReady && hasFrame
  const awaitingClip = !!clipRequest && !isArchive
  const awaitingFrames = streamReady && !hasFrame && !error
  const showChrome = streamReady || !!error || awaitingClip || awaitingFrames

  useEffect(() => {
    if (!connected || !channelGuid) return
    if (clipRequest) return
    if (isArchive) return
    startLive(channelGuid, quality)
  }, [connected, channelGuid, clipRequest, isArchive, quality, startLive])

  useEffect(() => {
    if (!connected || !channelGuid || !clipRequest) return
    if (clipKeyRef.current === clipRequest.key) return
    const ok = startArchivePlayback(channelGuid, {
      start: clipRequest.start,
      stop: clipRequest.stop,
      startUs: clipRequest.startUs,
      stopUs: clipRequest.stopUs,
      localAsUtc: clipRequest.localAsUtc,
    })
    // Ключ фиксируем только при успешной отправке — иначе при позднем WS не будет retry
    if (ok) clipKeyRef.current = clipRequest.key
  }, [connected, channelGuid, clipRequest, startArchivePlayback])

  useEffect(() => {
    if (!archiveEndSeq || archiveEndSeq === archiveEndHandledRef.current) return
    archiveEndHandledRef.current = archiveEndSeq
    clipKeyRef.current = null
    const t = setTimeout(() => {
      if (connected && channelGuid) startLive(channelGuid, quality)
      onClipEnded?.()
    }, error ? 1200 : 200)
    return () => clearTimeout(t)
  }, [archiveEndSeq, connected, channelGuid, startLive, quality, onClipEnded, error])

  const returnToLive = () => {
    clipKeyRef.current = null
    onClipEnded?.()
    if (connected && channelGuid) startLive(channelGuid, quality)
  }

  const onQualityChange = (id: VideoQualityId) => {
    setQuality(id)
    saveStoredVideoQuality(id)
  }

  const frameClass = [
    styles.frame,
    vpStyles.frameChrome,
    showChrome || chromePinned || isFullscreen ? vpStyles.chromeVisible : '',
    chromePinned || isFullscreen ? vpStyles.chromePinned : '',
    isZoomed ? vpStyles.viewportZoomed : '',
    dragging ? vpStyles.viewportDragging : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`${styles.wrap}${compact ? ` ${styles.wrapCompact}` : ''}`}>
      <div
        ref={viewportRef}
        className={frameClass}
        onPointerDown={(e) => {
          if (isZoomed) setDragging(true)
          onPointerDown(e)
        }}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => {
          setDragging(false)
          onPointerUp(e)
        }}
        onPointerCancel={(e) => {
          setDragging(false)
          onPointerCancel(e)
        }}
        onDoubleClick={onDoubleClick}
      >
        <div className={vpStyles.stage} style={stageStyle}>
          <canvas
            ref={canvasRef}
            className={styles.canvas}
            width={1280}
            height={720}
            style={{ opacity: playing ? 1 : 0 }}
          />
        </div>

        {!playing ? (
          <div className={styles.placeholder}>
            <p className={styles.placeholderTitle}>
              {error
                ? 'Нет видео'
                : awaitingClip || awaitingFrames
                  ? 'Архив…'
                  : 'Подключение…'}
            </p>
            <p className={styles.placeholderHint}>
              {error ||
                (awaitingFrames ? 'Ожидание кадров…' : status)}
            </p>
            {error ? (
              <Button size="small" icon={<ReloadOutlined />} onClick={() => reconnect()}>
                Повторить
              </Button>
            ) : null}
          </div>
        ) : null}

        <div className={vpStyles.chromeTop}>
          <div className={vpStyles.livePill}>
            <span
              className={
                isArchive || awaitingClip || awaitingFrames
                  ? vpStyles.archiveDot
                  : vpStyles.liveDot
              }
            />
            {isArchive || awaitingClip || awaitingFrames ? 'АРХИВ' : 'LIVE'}
          </div>
          <div className={vpStyles.chromeTitle}>
            {channelName || channelGuid}
            {isArchive || awaitingClip || awaitingFrames ? ` · ${status}` : ''}
          </div>
          <div className={vpStyles.chromeActions}>
            {isArchive || awaitingClip || awaitingFrames ? (
              <button
                type="button"
                className={vpStyles.liveReturnBtn}
                onPointerDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onClick={returnToLive}
              >
                Live
              </button>
            ) : null}
          </div>
        </div>

        <div
          className={vpStyles.chromeDock}
          onPointerEnter={() => setChromePinned(true)}
          onPointerLeave={() => setChromePinned(false)}
        >
          <div className={vpStyles.dockGlass}>
            {!isArchive && !awaitingClip && !awaitingFrames ? (
              <VideoQualityControls
                value={quality}
                disabled={!connected || !channelGuid}
                onChange={onQualityChange}
              />
            ) : null}
            <VideoViewportControls
              inline
              isFullscreen={isFullscreen}
              isZoomed={isZoomed}
              zoom={zoom}
              onToggleFullscreen={toggleFullscreen}
              onResetView={resetView}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
